import type { AbstractControl, ValidationErrors } from '@angular/forms';
import { containsDisallowedEmoji } from '../../../shared/validators/emoji.validators';

/** Maximum length for street address and optional address line fields. */
export const ADDRESS_LINE_MAX_LENGTH = 40;

/** Maximum length for Suite/Apt./Unit fields. */
export const SUITE_APT_UNIT_MAX_LENGTH = 10;

/** Maximum length for city fields. */
export const CITY_MAX_LENGTH = 40;

/** Maximum length for company or full name fields. */
export const COMPANY_OR_FULL_NAME_MAX_LENGTH = 30;

/** Maximum length for contact or attention-to name fields. */
export const CONTACT_NAME_MAX_LENGTH = 30;

/** Maximum length for email address fields. */
export const EMAIL_MAX_LENGTH = 60;

/** Restricted characters disallowed in company and contact name fields. */
export const NAME_RESTRICTED_CHARACTERS = '~*<>^{}"';

/** Restricted characters disallowed in street, address lines, and city fields. */
export const ADDRESS_RESTRICTED_CHARACTERS = String.raw`~|*<>^{}\/"`;

/** @deprecated Use ADDRESS_RESTRICTED_CHARACTERS. */
export const RESTRICTED_CHARACTERS = ADDRESS_RESTRICTED_CHARACTERS;

/** Matches name values that do not contain name restricted characters. */
export const NAME_RESTRICTED_CHARACTERS_REGEX = /^[^~*<>^{}"]*$/;

/** Matches address values that do not contain address restricted characters. */
export const ADDRESS_RESTRICTED_CHARACTERS_REGEX = /^[^~|*<>^{}\\/"]*$/;

/** @deprecated Use ADDRESS_RESTRICTED_CHARACTERS_REGEX. */
export const RESTRICTED_CHARACTERS_REGEX = ADDRESS_RESTRICTED_CHARACTERS_REGEX;

/** Error keys used by address form validators and error mappers. */
export const ADDRESS_VALIDATION_ERROR_KEYS = {
  required: 'required',
  emailInvalid: 'emailInvalid',
  postalCodeInvalid: 'postalCodeInvalid',
  phoneInvalid: 'phoneInvalid',
  phoneNonNumeric: 'phoneNonNumeric',
  phoneExtensionNonNumeric: 'phoneExtensionNonNumeric',
  phoneExtensionRequiresPhone: 'phoneExtensionRequiresPhone',
  addressUnserviceable: 'addressUnserviceable',
  addressNotFound: 'addressNotFound',
  nameRestrictedCharacters: 'nameRestrictedCharacters',
  restrictedCharacters: 'restrictedCharacters',
  entryBuzzerCodeInvalid: 'entryBuzzerCodeInvalid',
  civicAddressRequired: 'civicAddressRequired',
  phoneRequiredPostalKeyword: 'phoneRequiredPostalKeyword',
  civicStreetRequired: 'civicStreetRequired',
} as const;

/** Legacy OLS email regex from GlobalConstants.REGEX_EMAIL. */
export const LEGACY_EMAIL_REGEX =
  /^([a-zA-Z0-9][\w.-]*[a-zA-Z0-9_.-]|[a-zA-Z0-9])@[a-zA-Z0-9][\w.-]*[a-zA-Z0-9]\.[a-zA-Z][a-zA-Z.]*[a-zA-Z]$/;

const MIN_EMAIL_LENGTH = 3;

/**
 * Validates non-empty trimmed values for required address fields.
 */
export function requiredTrimmedValidator(control: AbstractControl): ValidationErrors | null {
  const value = typeof control.value === 'string' ? control.value.trim() : control.value;

  if (value === null || value === undefined || value === '') {
    return { [ADDRESS_VALIDATION_ERROR_KEYS.required]: true };
  }

  return null;
}

/**
 * Validates email format using the legacy OLS regex when a value is present.
 */
export function optionalEmailFormatValidator(control: AbstractControl): ValidationErrors | null {
  const value = typeof control.value === 'string' ? control.value.trim() : '';

  if (!value) {
    return null;
  }

  if (value.length > EMAIL_MAX_LENGTH) {
    return null;
  }

  return isLegacyValidEmail(value) ? null : { [ADDRESS_VALIDATION_ERROR_KEYS.emailInvalid]: true };
}

/**
 * Mirrors legacy GlobalUtility.IsValidEmail for non-empty values.
 */
export function isLegacyValidEmail(email: string): boolean {
  const localEmail = email.trim();

  if (localEmail.length < MIN_EMAIL_LENGTH || localEmail.length > EMAIL_MAX_LENGTH) {
    return false;
  }

  return LEGACY_EMAIL_REGEX.test(localEmail);
}

/**
 * Required email validator for address sections where email is mandatory.
 */
export function requiredEmailValidator(control: AbstractControl): ValidationErrors | null {
  const requiredError = requiredTrimmedValidator(control);

  if (requiredError) {
    return requiredError;
  }

  return optionalEmailFormatValidator(control);
}

/**
 * Returns whether the value contains only allowed characters for name fields.
 * Rejects name denylist punctuation and emoji (OM-718).
 */
export function isAllowedNameFieldText(value: string): boolean {
  return NAME_RESTRICTED_CHARACTERS_REGEX.test(value) && !containsDisallowedEmoji(value);
}

/**
 * Returns whether the value contains only allowed characters for address text fields.
 * Rejects address denylist punctuation and emoji (OM-718).
 */
export function isAllowedAddressFieldText(value: string): boolean {
  return ADDRESS_RESTRICTED_CHARACTERS_REGEX.test(value) && !containsDisallowedEmoji(value);
}

/**
 * @deprecated Use isAllowedAddressFieldText.
 */
export function isAllowedRestrictedFieldText(value: string): boolean {
  return isAllowedAddressFieldText(value);
}

/**
 * Rejects values that contain name restricted characters.
 */
export function nameRestrictedCharactersValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = typeof control.value === 'string' ? control.value : '';

  return isAllowedNameFieldText(value)
    ? null
    : { [ADDRESS_VALIDATION_ERROR_KEYS.nameRestrictedCharacters]: true };
}

/**
 * Rejects values that contain address restricted characters.
 */
export function addressRestrictedCharactersValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = typeof control.value === 'string' ? control.value : '';

  return isAllowedAddressFieldText(value)
    ? null
    : { [ADDRESS_VALIDATION_ERROR_KEYS.restrictedCharacters]: true };
}

/**
 * @deprecated Use addressRestrictedCharactersValidator.
 */
export function restrictedCharactersValidator(control: AbstractControl): ValidationErrors | null {
  return addressRestrictedCharactersValidator(control);
}

/**
 * Validates optional contact names against name restricted characters when a value is present.
 */
export function optionalNameRestrictedCharactersValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = typeof control.value === 'string' ? control.value.trim() : '';

  if (!value) {
    return null;
  }

  return nameRestrictedCharactersValidator(control);
}

/**
 * Validates optional address text fields against address restricted characters when present.
 */
export function optionalAddressRestrictedCharactersValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = typeof control.value === 'string' ? control.value.trim() : '';

  if (!value) {
    return null;
  }

  return addressRestrictedCharactersValidator(control);
}

/**
 * @deprecated Use optionalNameRestrictedCharactersValidator.
 */
export function optionalRestrictedCharactersValidator(
  control: AbstractControl,
): ValidationErrors | null {
  return optionalNameRestrictedCharactersValidator(control);
}
